import { HOUR_MS } from '../kpowFormat';

export type KpowDisplayParty = 'D' | 'R' | 'Even';

/** Symmetric display + numeric envelope for the composite and per-component scores. */
export interface KpowScore {
    signed_value: number;
    display_party: KpowDisplayParty;
    display_value: number;
}

export interface KpowComponent {
    name: string;
    /** Null when the component was unavailable at snapshot time. */
    score: KpowScore | null;
    weight: number;
}

/** Response shape of GET /v1/cached/indexes/KPOW. */
export interface KpowSnapshot {
    /** Unix seconds. */
    data_as_of: number;
    composite: KpowScore;
    components: KpowComponent[];
}

/** History points carry the same display envelope as the composite snapshot, plus a timestamp. */
export interface KpowHistoryPoint extends KpowScore {
    /** Unix seconds. */
    end_period_ts: number;
}

/** Response shape of GET /v1/cached/indexes/KPOW/history. */
export interface KpowHistory {
    points: KpowHistoryPoint[];
}

/**
 * Client calls hit our same-origin edge, which rewrites to
 * `https://api.elections.kalshi.com/v1/cached/indexes/KPOW` (see
 * `rewrites()` in `next.config.js`). This sidesteps the CORS + AWS-WAF
 * challenge restrictions that the prod API enforces against non-kalshi.com
 * origins, so preview deploys + local dev can load real data without
 * special config.
 */
const KPOW_BASE = '/api/kpow';

/** First hour of history shown on the KPOW charts. */
export const KPOW_HISTORY_START_MS = Date.UTC(2026, 0, 1);

/**
 * The history endpoint rejects any request spanning more than 5000 hours, so longer ranges are split
 * into windows. Kept below the cap so the open-ended last window, whose end the server resolves to
 * "now", can't cross it through clock skew.
 */
const HISTORY_REQUEST_SPAN_MS = 4800 * HOUR_MS;

export interface HistoryRequestWindow {
    /** Unix seconds, floored to the hour so request URLs stay stable across reads. */
    startTs: number;
    /** Unix seconds. Omitted on the last window so the newest hourly point stays live. */
    endTs?: number;
}

/** Contiguous request windows covering `startMs` through `nowMs`, each within the endpoint's span cap. */
export const historyRequestWindows = (startMs: number, nowMs: number): HistoryRequestWindow[] => {
    const windows: HistoryRequestWindow[] = [];
    let windowStartMs = Math.floor(startMs / HOUR_MS) * HOUR_MS;
    while (nowMs - windowStartMs > HISTORY_REQUEST_SPAN_MS) {
        const windowEndMs = windowStartMs + HISTORY_REQUEST_SPAN_MS;
        windows.push({ startTs: windowStartMs / 1000, endTs: windowEndMs / 1000 });
        windowStartMs = windowEndMs;
    }
    windows.push({ startTs: windowStartMs / 1000 });
    return windows;
};

/**
 * Joins ascending chunks into one ascending series. Both request bounds are inclusive, so adjacent
 * windows share their boundary hour; any point not newer than the last one kept is dropped.
 */
export const mergeHistoryChunks = (chunks: KpowHistoryPoint[][]): KpowHistoryPoint[] => {
    const merged: KpowHistoryPoint[] = [];
    chunks.forEach((chunk) => {
        chunk.forEach((point) => {
            const last = merged[merged.length - 1];
            if (last === undefined || point.end_period_ts > last.end_period_ts) merged.push(point);
        });
    });
    return merged;
};

async function fetchKpow<T>(path: string): Promise<T> {
    // Calling our own edge (the rewrite proxies to api.elections.kalshi.com),
    // so the AWS-WAF challenge wrapper isn't needed and would add a no-op token fetch.
    // eslint-disable-next-line no-restricted-globals
    const res = await fetch(path);
    if (!res.ok) throw new Error(`KPOW API error ${res.status} for ${path}`);
    return res.json() as Promise<T>;
}

export async function fetchKpowSnapshot(): Promise<KpowSnapshot> {
    return fetchKpow<KpowSnapshot>(KPOW_BASE);
}

export async function fetchKpowHistory(): Promise<KpowHistory> {
    const windows = historyRequestWindows(KPOW_HISTORY_START_MS, Date.now());
    const chunks = await Promise.all(
        windows.map(({ startTs, endTs }) =>
            fetchKpow<KpowHistory>(
                `${KPOW_BASE}/history?start_ts=${startTs}${endTs === undefined ? '' : `&end_ts=${endTs}`}`,
            ),
        ),
    );
    return { points: mergeHistoryChunks(chunks.map((chunk) => chunk.points)) };
}
